import { useState } from 'react'
import { addressService } from 'src/services/addressService'
import type { OrderForm } from 'src/sdk/checkout/entity/orderForm'

import type { LocalAddressesPayload } from '../domain/addressManagerReconciliation'

interface UsePostalCodeAddressCardActionsParams {
  addressId: string
  orderFormId: string
  syncOrderFormFn: (orderForm: OrderForm) => void
  onMakeDefault?: () => void
  onSelect?: () => void
  onUpdateLocalAddresses?: (payload: LocalAddressesPayload) => void
}

function resolveSelectedShippingAddressId(
  shippingData: NonNullable<OrderForm['shippingData']>
): string | null {
  return (
    shippingData.address?.addressId ??
    shippingData.selectedAddresses?.[0]?.addressId ??
    null
  )
}

async function persistDeleteAndNotifyLocalAddresses({
  addressId,
  orderFormId,
  syncOrderFormFn,
  onUpdateLocalAddresses,
}: {
  addressId: string
  orderFormId: string
  syncOrderFormFn: (orderForm: OrderForm) => void
  onUpdateLocalAddresses?: UsePostalCodeAddressCardActionsParams['onUpdateLocalAddresses']
}) {
  const updatedOrderForm = await addressService.deleteAddress({
    addressId,
    orderFormId,
    syncOrderFormFn,
  })

  const shipping = updatedOrderForm?.shippingData

  if (!onUpdateLocalAddresses || !shipping?.availableAddresses) {
    return
  }

  onUpdateLocalAddresses({
    updatedAddresses: shipping.availableAddresses,
    selectedAddressId: resolveSelectedShippingAddressId(shipping),
  })
}

export function usePostalCodeAddressCardActions({
  addressId,
  orderFormId,
  syncOrderFormFn,
  onMakeDefault,
  onSelect,
  onUpdateLocalAddresses,
}: UsePostalCodeAddressCardActionsParams) {
  const [isDeletePending, setIsDeletePending] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isMakingDefault, setIsMakingDefault] = useState(false)

  const isActionsBusy = isDeleting || isMakingDefault

  const handleExcludeClick = async () => {
    if (isActionsBusy) {
      return
    }

    setIsDeleting(true)

    try {
      await persistDeleteAndNotifyLocalAddresses({
        addressId,
        orderFormId,
        syncOrderFormFn,
        onUpdateLocalAddresses,
      })
    } catch (error) {
      console.error('Error deleting address:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleMakeDefault = async () => {
    if (isActionsBusy) {
      return
    }

    setIsMakingDefault(true)

    try {
      await addressService.setDefaultAddress({
        addressId,
        orderFormId,
      })

      await addressService.selectAddress({
        addressId,
        orderFormId,
        syncOrderFormFn,
      })

      onMakeDefault?.()
      onSelect?.()
    } catch (error) {
      console.error('Error setting default address:', error)
    } finally {
      setIsMakingDefault(false)
    }
  }

  const handleSelect = async () => {
    try {
      await addressService.selectAddress({
        addressId,
        orderFormId,
        syncOrderFormFn,
      })
      onSelect?.()
    } catch (error) {
      console.error('Error selecting address:', error)
    }
  }

  return {
    isDeletePending,
    setIsDeletePending,
    isDeleting,
    isMakingDefault,
    isActionsBusy,
    handleExcludeClick,
    handleMakeDefault,
    handleSelect,
  }
}
